<style scoped lang="scss">
  .payment-form {
    position: relative;

    .loading-container {
      @include position-all(absolute, 0);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(white, .5);
    }

    .payment-title {
      font-weight: 600;
      text-transform: uppercase;
    }

    .payment-methods {
      margin-top: 20px;
    }

    .payment-inputs {
      margin-top: 20px;

      .form-group + .form-group {
        margin-top: 20px;
      }
    }

    .payment-total {
      margin-top: 20px;

      .total-text {
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0;
      }

      .total-amount {
        margin-top: 10px;
        font-size: 24px;
        line-height: 36px;
      }
    }

    .missing-amount {
      margin-top: 20px;
    }

    .payment-error {
      margin-top: 10px;
    }

    .payment-controls {
      margin-top: 20px;
    }

    .payment-legal-text {
      margin-top: 15px;
      font-size: 12px;
      color: #838383;
    }

    .phone-legal-documents {
      margin-top: 30px;
    }

    .additional-actions-message {
      margin-top: 15px;
    }
  }
</style>

<template>
  <div class="payment-form">
    <template v-if="additionalActionsText">
      <div class="payment-title">{{ $t('payment.additionalActions') }}</div>

      <info-box class="additional-actions-message">
        {{ additionalActionsText }}
      </info-box>
    </template>
    <template v-else>
      <div class="payment-title">{{ $t('payment.choosePaymentMethod') }}</div>

      <payment-methods/>

      <div v-if="displayForm" class="payment-inputs">
        <form-group
          :state="!errors.email"
          :invalid-feedback="errors.email">
          <advanced-input
            v-model="email"
            @input="onFieldInput('email')"
            icon-left="email"
            :placeholder="$t('payment.form.email')"
            @change="onEmailChange"/>
        </form-group>

        <form-group
          v-if="displayQiwiWalletInput"
          :state="!errors.phone"
          :invalid-feedback="errors.phone">
          <advanced-input
            v-model="qiwiWallet"
            @input="onFieldInput('qiwi')"
            icon-left="wallet"
            :placeholder="$t('payment.form.qiwiWallet')"/>
        </form-group>

        <form-group
          v-if="displayPhoneInput"
          :state="!errors.phone"
          :invalid-feedback="errors.phone">
          <advanced-input
            v-model="phone"
            @input="onFieldInput('phone')"
            icon-left="phone"
            :placeholder="$t('payment.form.phone')"/>
        </form-group>

        <form-group
          v-if="displayRegionSelect"
          :state="!errors.country"
          :invalid-feedback="errors.country">
          <payment-region-select/>
        </form-group>
      </div>

      <template v-if="displayTotal">
        <div class="payment-total">
          <div class="total-text">{{ $t('payment.total') }}</div>
          <div class="total-amount">{{ totalAmountText }}</div>
        </div>

        <info-box v-if="errors.request" class="payment-error" variant="error">
          {{ errors.request }}
        </info-box>
      </template>

      <div v-if="paymentMethod" class="payment-controls">
        <template v-if="displayDefaultPayButton">
          <btn
            icon-left="gift"
            variant="primary"
            size="lg"
            :disabled="loaderVisible"
            @click="processDefaultPay">{{ $t('global.common.send') }}
          </btn>
        </template>
        <template v-else>
          <apple-pay-button
            v-if="paymentMethod === 'applePay'"
            @click="processApplePay"/>
          <google-pay-button
            v-if="paymentMethod === 'googlePay'"
            @click="processGooglePay"/>
        </template>
      </div>
    </template>

    <div class="payment-legal-text">{{ $t('payment.legal') }}</div>

    <phone-legal-documents v-if="paymentMethod === 'fakeMobile'"/>

    <div v-if="loaderVisible" class="loading-container">
      <loader size="lg"/>
    </div>
  </div>
</template>

<script>

import applePay from '@services/apple-pay'
import googlePay from '@services/google-pay'

import PaymentMethods from './PaymentMethods'
import GooglePayButton from './GooglePayButton'
import ApplePayButton from './ApplePayButton'
import PaymentRegionSelect from './PaymentRegionSelect'
import PhoneLegalDocuments from './PhoneLegalDocuments'
import storage from '@utils/storage'

export default {
  name: 'PaymentForm',
  components: {
    PhoneLegalDocuments,
    PaymentRegionSelect,
    ApplePayButton,
    GooglePayButton,
    PaymentMethods,
  },
  data() {
    return {
      loadingState: {
        finalAmount: false,
        invoice: false,
      },

      totalAmount: 0,
      totalAmountCurrency: 'RUB',

      errors: {},

      additionalActionsText: null,

      // Invoice
      pollingInvoice: false,
      pollingInvoiceTimeout: null,

      pollingAttemptsLeft: 40,
    }
  },
  computed: {
    ...mapGetters('currencies', ['getAmountWithCurrency']),

    ...mapState('payment', ['paymentMethod', 'paymentData']),
    ...mapState('invoice', ['invoice']),

    ...mapGetters('payment', ['selectedPaymentMethod', 'paymentRegionsList']),

    ...mapFields('payer', ['phone', 'email', 'qiwiWallet']),

    loaderVisible() {
      return this.loadingState.finalAmount || this.loadingState.invoice
    },

    totalAmountText() {
      return this.selectedPaymentMethod
        ? this.getAmountWithCurrency(this.totalAmount, this.totalAmountCurrency)
        : null
    },

    displayForm() {
      return !!this.selectedPaymentMethod
    },

    displayRegionSelect() {
      return [
        'adyenSofortEur',
        'trustly',
      ].includes(this.paymentMethod) && !!this.paymentRegionsList.length
    },

    displayTotal() {
      return !!this.selectedPaymentMethod && !!this.totalAmount
    },

    displayPhoneInput() {
      if (this.paymentMethod === 'sberPaySmgl') {
        return this.$appIsLG
      }
      return ['fakeMobile', 'fakeMobileDobro', 'sberPay'].includes(this.paymentMethod)
    },

    displayQiwiWalletInput() {
      return ['qiwiMyCom', 'qiwiDobro'].includes(this.paymentMethod)
    },

    displayDefaultPayButton() {
      return !['applePay', 'googlePay'].includes(this.paymentMethod)
    },
  },
  methods: {
    ...mapActions('invoice', ['createInvoice', 'checkInvoice']),
    ...mapActions('payment', ['fetchFinalAmount', 'fetchPaymentRegions']),

    onFieldInput(field) {
      this.$set(this.errors, field, null)
    },

    onEmailChange() {
      this.$tmr.goal('input_email_changed')
    },

    logPaymentProcess() {
      this.$ga.action({
        category: 'DonationPage-PaymentMethods',
        action: 'btn_checkout',
        label: this.paymentMethod,
      })

      this.$ga.action({
        category: 'AB_DonationPage',
        action: 'btn_checkout',
        label: this.paymentMethod,
      })

      this.$tmr.event({
        category: 'AB_DonationPage',
        action: 'new_action_btn_checkout',
        label: this.paymentData.userid,
      })

      this.$tmr.goal('pay_clicked', {
        paymentSystem: this.paymentMethod,
      })
    },

    processGooglePay() {
      this.logPaymentProcess()

      this.loadingState.invoice = true

      this.errors = {}

      googlePay.pay(this.totalAmountCurrency, this.totalAmount)
        .then(paymentToken => {
          this.processInvoice(paymentToken)
        })
        .catch(() => {
          console.log('[Google Pay] Cancelled')
        })
        .finally(() => {
          this.loadingState.invoice = false
        })
    },

    processApplePay() {
      this.logPaymentProcess()

      this.loadingState.invoice = true

      this.errors = {}

      applePay.pay(this.totalAmountCurrency, this.totalAmount, false)
        .then(paymentToken => {
          this.processInvoice(paymentToken)
        })
        .catch(() => {
          console.log('[Apple Pay] Cancelled')
        })
        .finally(() => {
          this.loadingState.invoice = false
        })
    },

    processDefaultPay() {
      this.logPaymentProcess()

      if (this.paymentMethod === 'sberPaySmgl') {
        this.pollingAttemptsLeft = 600
      }

      this.processInvoice()
    },

    processInvoice(paymentToken = null) {
      this.loadingState.invoice = true

      this.errors = {}

      storage.setItem('da.payin.payment.errorMessage', null)

      return this.createInvoice(paymentToken ? { paymentToken } : null)
        .then(({ invoice, action, redirect_url, text }) => {
          this.$tmr.event({
            category: 'AB_DonationPage',
            action: 'new_action_start_pay_process',
            label: invoice.id,
          })
          this.processInvoiceAction({ action, redirect_url, text })
        })
        .catch(({ errors, code, message }) => {
          this.loadingState.invoice = false

          this.$tmr.goal('payment_failed', {
            paymentSystem: this.paymentMethod,
          })

          if (Number(code) === 2205) {
            storage.setItem('da.payin.payment.errorMessage', message)
            this.$router.replace({ name: 'payment-error-page' })
          } else if (errors) {
            errors.forEach(e => {
              this.$set(this.errors, e.param, e.message)
            })
          }
        })
    },

    processInvoiceAction({ action, redirect_url, text }) {
      if (action === 'redirect') {
        window.location.href = redirect_url
      }

      if (action === 'wait') {
        this.startInvoicePolling()
      }

      if (action === 'close') {
        this.loadingState.invoice = false

        if (['googlePay', 'applePay'].includes(this.paymentMethod)) {
          return this.$router.replace({ name: 'payment-success-page' })
        }

        this.additionalActionsText = text
      }
    },

    startInvoicePolling() {
      this.pollingAttemptsLeft = this.pollingAttemptsLeft - 1

      if (this.pollingAttemptsLeft <= 0) {
        this.loadingState.invoice = false

        return this.$set(this.errors, 'request', this.$t('payment.form.timeoutError'))
      }

      this.pollingInvoiceTimeout = setTimeout(() => {
        this.checkInvoice()
          .then(({ action, redirect_url, text }) => {
            this.processInvoiceAction({ action, redirect_url, text })
          })
      }, 2000)
    },

    calculateFinalAmount() {
      this.totalAmount = 0

      if (this.paymentMethod) {
        this.loadingState.finalAmount = true

        this.fetchFinalAmount()
          .then(({ amount, currencyCode }) => {
            this.totalAmount = amount
            this.totalAmountCurrency = currencyCode
          })
          .finally(() => {
            this.loadingState.finalAmount = false
          })
      }
    },

    async onPaymentMethodChange() {
      if (this.paymentMethod) {
        if (this.paymentMethod === 'adyenSofortEur') {
          this.loadingState.finalAmount = true
          await this.fetchPaymentRegions(['klarna'])
          this.loadingState.finalAmount = false
        }
      }

      return this.calculateFinalAmount()
    },
  },
  created() {
    this.$tmr.goal('checkout')
  },
  watch: {
    paymentMethod: {
      handler: 'onPaymentMethodChange',
      immediate: true,
    },
  },
}
</script>
